<template>
  <div class="premium-select-wrap">
    <label v-if="label" class="premium-label">{{ label }}</label>

    <div
      class="premium-select-field"
      :class="{ 'has-icon': !!icon, 'wrap-readonly': disabled || readonly }"
    >
      <!-- Icon above overlay — visible in add + edit -->
      <div v-if="icon" class="field-icon-rail">
        <v-icon size="18">{{ icon }}</v-icon>
      </div>

      <!-- Multiple select (v-select with chips) -->
      <v-select
        v-if="multiple"
        v-model="internalValue"
        :items="items"
        :item-text="itemText"
        :item-value="itemValue"
        :placeholder="!internalValue || internalValue.length === 0 ? placeholder : ''"
        outlined
        dense
        hide-details="auto"
        multiple
        class="premium-select"
        :class="{ 'is-dark': isDark, 'is-readonly': disabled || readonly, 'has-icon': !!icon }"
        v-bind="filteredAttrs"
        :error-messages="error"
        :error="!!error"
        v-on="$listeners"
        :menu-props="{
          contentClass: isDark ? 'premium-menu dark-menu' : 'premium-menu',
          offsetY: true,
          nudgeBottom: 4,
        }"
      >
        <template v-slot:selection="{ item, index }">
          <v-chip v-if="index < 3" small class="chip-premium">
            {{ typeof item === 'object' ? item[itemText] : item }}
          </v-chip>
          <span v-if="index === 3" class="grey--text caption ml-1">
            (+{{ internalValue.length - 3 }} more)
          </span>
        </template>
        <template v-slot:item="{ item, attrs, on }">
          <v-list-item v-on="on" v-bind="attrs" class="premium-list-item">
            <v-list-item-action class="mr-2">
              <v-checkbox :input-value="attrs.inputValue" color="var(--brand, #10B981)" dense hide-details />
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title class="item-title">
                {{ typeof item === 'object' ? item[itemText] : item }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-select>

      <!-- Single select (v-autocomplete) -->
      <v-autocomplete
        v-else
        v-model="internalValue"
        :items="items"
        :item-text="itemText"
        :item-value="itemValue"
        :placeholder="placeholder"
        outlined
        dense
        hide-details="auto"
        class="premium-select"
        :class="{ 'is-dark': isDark, 'is-readonly': disabled || readonly, 'has-icon': !!icon }"
        v-bind="filteredAttrs"
        :error-messages="error"
        :error="!!error"
        v-on="$listeners"
        :menu-props="{
          contentClass: isDark ? 'premium-menu dark-menu' : 'premium-menu',
          offsetY: true,
          nudgeBottom: 4,
        }"
      >
        <template v-slot:selection="{ item, index }">
          <span v-if="index === 0" class="selection-text">
            {{ typeof item === 'object' ? item[itemText] : item }}
          </span>
        </template>
        <template v-slot:item="{ item, attrs, on }">
          <v-list-item v-on="on" v-bind="attrs" class="premium-list-item">
            <v-list-item-content>
              <v-list-item-title class="item-title">
                {{ typeof item === 'object' ? item[itemText] : item }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-autocomplete>

      <!-- Blocks interaction in edit mode without hiding icons -->
      <div v-if="disabled || readonly" class="readonly-overlay"></div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'PremiumSelect',
  inheritAttrs: false,

  props: {
    value: { default: null },
    label: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    itemText: { type: String, default: 'text' },
    itemValue: { type: String, default: 'value' },
    placeholder: { type: String, default: 'Select option' },
    icon: { type: String, default: '' },
    error: { type: [String, Array], default: '' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
  },

  computed: {
    ...mapState('theme', ['isDark', 'primaryColor']),
    internalValue: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },
    filteredAttrs() {
      const rest = { ...this.$attrs };
      delete rest.disabled;
      delete rest.readonly;
      return rest;
    
    },
    isLocked() {
      return this.$attrs.disabled === true || this.$attrs.disabled === '';
    },
    hasValue() {
      const v = this.internalValue;
      return v !== null && v !== undefined && v !== '';
    },
    effectivePlaceholder() {
      return this.hasValue ? '' : this.placeholder;
    },
    selectedLabel() {
      if (!this.hasValue) return '';
      const found = this.items.find(
        (i) => String(i[this.itemValue]) === String(this.internalValue)
      );
      if (found) return found[this.itemText];
      return String(this.internalValue);
    },
  },

  methods: {
    resolveItemText(item) {
      if (item && typeof item === 'object') return item[this.itemText];
      return this.selectedLabel;
    },
  },
};
</script>

<style scoped>
.premium-select-wrap {
  margin-bottom: 2px;
}

.premium-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 4px;
}

.premium-select-field {
  position: relative;
  --premium-field-h: 46px;
}

/* External icon rail — align to input control only (not error message area) */
.field-icon-rail {
  position: absolute;
  left: 0;
  top: 0;
  height: var(--premium-field-h);
  width: 40px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.field-icon-rail .v-icon {
  font-size: 18px !important;
  color: var(--tx3, #94a3b8) !important;
  opacity: 1 !important;
}

.premium-select.has-icon >>> .v-input__prepend-inner {
  display: none !important;
  width: 0 !important;
  min-width: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
}

.premium-select.has-icon >>> .v-input__slot {
  padding-left: 40px !important;
}

/* Match PremiumInput — icon + text vertically centered in field */
.premium-select >>> .v-input__control {
  min-height: var(--premium-field-h, 46px) !important;
}

.premium-select >>> .v-input__slot {
  align-items: center !important;
  min-height: var(--premium-field-h, 46px) !important;
}

.premium-select >>> .v-select__slot {
  align-items: center !important;
  min-height: 32px !important;
}

.premium-select >>> .v-select__selections {
  align-items: center !important;
  flex-wrap: wrap;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  min-height: 32px !important;
}

.premium-select >>> input {
  align-self: center !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0 !important;
  font-size: 13.5px !important;
  font-weight: 500 !important;
}

.premium-select >>> .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
}

.premium-select >>> .chip-premium {
  margin-top: 2px !important;
  margin-bottom: 2px !important;
}

.premium-select >>> fieldset {
  border-width: 1px !important;
  border-color: var(--border, rgba(0, 0, 0, 0.1)) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.premium-select.v-input--is-focused >>> fieldset {
  border-color: var(--brand, #10b981) !important;
  border-width: 1.5px !important;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.12) !important;
}

.selection-text {
  font-size: 13.5px;
  font-weight: 500;
  color: var(--tx, #111827);
}

.is-dark .selection-text {
  color: #FFFFFF !important;
}

.theme--dark .premium-select >>> .v-input__slot {
  background: #262626 !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.theme--dark .premium-select >>> .v-input__slot:hover {
  border-color: rgba(255, 255, 255, 0.25) !important;
}

.theme--dark .premium-select.v-input--is-focused >>> fieldset {
  border-color: var(--brand, #E5E5E5) !important;
  background: #2a2a2a !important;
}

.premium-select >>> .v-icon {
  font-size: 18px !important;
  color: #A3A3A3 !important;
  transition: color 0.2s ease;
}

.theme--dark .premium-select >>> .v-icon {
  color: #A3A3A3 !important;
}

.premium-select.v-input--is-focused >>> .v-icon {
  color: var(--brand, #10b981) !important;
}

.theme--dark .premium-select.v-input--is-focused >>> .v-icon {
  color: var(--brand, #E5E5E5) !important;
}

.premium-select.v-input--has-state.error--text >>> fieldset {
  border-color: #ef4444 !important;
}

/* Readonly / disabled look (edit mode) */
.premium-select.is-readonly >>> fieldset {
  border-color: var(--border, rgba(0, 0, 0, 0.08)) !important;
  background-color: var(--surface2, #f8fafc) !important;
}

.premium-select.is-readonly >>> .v-input__slot {
  background-color: var(--surface2, #f8fafc) !important;
  cursor: not-allowed !important;
}

.premium-select.is-readonly >>> input,
.premium-select.is-readonly >>> .selection-text {
  color: var(--tx2, #374151) !important;
}

.theme--dark .premium-select.is-readonly >>> fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
  background-color: #1F1F1F !important;
}

.theme--dark .premium-select.is-readonly >>> .v-input__slot {
  background-color: #1F1F1F !important;
}

.readonly-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  cursor: not-allowed;
  border-radius: 8px;
}

.wrap-readonly .field-icon-rail {
  z-index: 5;
}

.wrap-readonly .premium-select >>> .v-input__append-inner {
  opacity: 0.5 !important;
}

/* Disabled/locked selects keep same look as block filter (dropdown arrow visible) */
.premium-select--locked >>> .v-input__append-inner {
  display: flex !important;
  opacity: 1 !important;
}

.premium-select--locked >>> fieldset {
  border-color: var(--border, rgba(0,0,0,0.1)) !important;
  background: #fff !important;
}

.premium-select--locked >>> input {
  color: var(--tx, #111827) !important;
  -webkit-text-fill-color: var(--tx, #111827) !important;
  font-weight: 500;
}

.premium-select--locked >>> .v-input__slot {
  cursor: default !important;
}

.premium-select.has-value >>> input::placeholder,
.premium-select--locked.has-value >>> input::placeholder {
  opacity: 0 !important;
  color: transparent !important;
}
</style>

<style>
.chip-premium {
  background: var(--brand, #0d9972) !important;
  color: #fff !important;
  font-weight: 700 !important;
  font-size: 11px !important;
  margin: 2px !important;
}

.premium-menu {
  border-radius: 12px !important;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid var(--border) !important;
  background-color: var(--surface) !important;
  overflow-y: auto !important;
  max-height: 300px !important;
  margin-top: 4px !important;
}

.premium-menu.dark-menu {
  background-color: var(--surface) !important;
  border-color: var(--border) !important;
}

.premium-list-item {
  min-height: 40px !important;
  padding: 0 16px !important;
  border-radius: 8px !important;
  margin: 2px 6px !important;
  transition: all 0.2s ease !important;
}

.item-title {
  font-size: 13.5px !important;
  font-weight: 600 !important;
  color: var(--tx2) !important;
}

.premium-menu .v-list-item:hover {
  background-color: var(--brand-lt) !important;
}

.premium-menu .v-list-item:hover .item-title {
  color: var(--brand) !important;
}

.premium-menu .v-list-item--active,
.premium-menu .v-list-item--highlighted {
  background-color: var(--brand-lt) !important;
}

.premium-menu .v-list-item--active .item-title,
.premium-menu .v-list-item--highlighted .item-title {
  font-weight: 700 !important;
  color: var(--brand) !important;
}

.premium-menu .v-list-item--active::before,
.premium-menu .v-list-item--highlighted::before {
  opacity: 0 !important;
}
</style>
